/**
 * Retailer admin API client — /api/retailer-admin-*.php
 *
 * The signed-in user's own retailer area (/retaileradmin). Every call is
 * credentialed; writes go through csrfFetch (X-Auth-CSRF + origin).
 */

import { csrfFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

/** viewer | manager. Both apply across the user's location set (or every location with the all-locations switch). */
export type RetailerRole = 'viewer' | 'manager';
export interface RetailerLocationRef { id: number; name: string; city: string | null }

export interface RetailerAdminMembership {
  retailerId: number;
  retailerName: string;
  role: RetailerRole;
}

export interface RetailerAdminContext {
  retailerId: number;
  retailerName: string;
  role: RetailerRole;
  canView: boolean;
  canManage: boolean;
  /** The whole node: every location, incl. ones added later. */
  allLocations: boolean;
  /** With allLocations on a top-level retailer: its sub-retailers too. */
  includeChildren: boolean;
  /** Same as allLocations (kept for older callers). */
  wholeRetailer: boolean;
  /** all = whole node · locations = a set · none = a set whose stores all left. */
  scope: 'all' | 'locations' | 'none';
  /** The locations this user holds (empty with allLocations). */
  locations: RetailerLocationRef[];
  childRetailerIds: number[];
  memberships: RetailerAdminMembership[];
}

export interface RetailerEstimateRow {
  id: number;
  guid: string;
  estimate_name: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  user_category: string | null;
  project_type: string | null;
  country: string | null;
  region: string | null;
  city: string | null;
  postal_code: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  space_type: string | null;
  calculation_mode: string | null;
  views: number;
  comments: number;
  shares: number;
  user_id: number | null;
  retailer_id: number | null;
  retailer_name: string | null;
  prepared_by_user_id?: number | null;
  prepared_by_label?: string | null;
}

export interface EstimateStats {
  today: number;
  yesterday: number;
  this_week: number;
  last_week: number;
  this_month: number;
  last_month: number;
  total: number;
}

export interface Pagination {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

export interface RetailerEstimatesResponse {
  estimates: RetailerEstimateRow[];
  pagination: Pagination;
  stats: EstimateStats;
}

export interface RetailerEstimatePatch {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  estimateName?: string | null;
}

export interface RetailerEstimateView {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  estimateName: string | null;
}

export interface RetailerShareRow {
  id: number;
  /** The share's CHILD guid — opening it shows the estimate priced for the store. */
  guid: string;
  estimateName: string | null;
  customerName: string;
  city: string | null;
  region: string | null;
  locationName: string;
  sentAt: string;
  firstViewedAt: string | null;
  commentCount: number;
  unread: number;
}

export interface RetailerDashboardResponse {
  stats: EstimateStats;
  recent: RetailerEstimateRow[];
  shares: RetailerShareRow[];
  pendingCount: number | null;
  memberCount: number;
}

/** Thrown for 403 { error: 'not_member' | 'manager_required' }. */
export class RetailerAccessError extends Error {
  constructor(public code: string, status: number) {
    super(code);
    this.name = 'RetailerAccessError';
    this.status = status;
  }
  status: number;
}

async function parse<T>(res: Response, fallback: string): Promise<T> {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const code = (data as { error?: string }).error || fallback;
    if (res.status === 403 && (code === 'not_member' || code === 'manager_required')) throw new RetailerAccessError(code, res.status);
    const err = new Error(code) as Error & { status?: number };
    err.status = res.status;
    throw err;
  }
  return data as T;
}

export async function fetchRetailerContext(retailerId?: number): Promise<RetailerAdminContext> {
  const q = retailerId ? `?retailer=${retailerId}` : '';
  const res = await fetch(`${API_URL}/api/retailer-admin-context.php${q}`, { credentials: 'include' });
  return (await parse<{ context: RetailerAdminContext }>(res, 'context_failed')).context;
}

export async function switchRetailer(retailerId: number): Promise<RetailerAdminContext> {
  const res = await csrfFetch(`${API_URL}/api/retailer-admin-context.php`, { jsonBody: { retailerId } });
  return (await parse<{ context: RetailerAdminContext }>(res, 'switch_failed')).context;
}

export interface EstimateListQuery {
  page: number;
  perPage: number;
  search: string;
  sort: string;
  order: 'ASC' | 'DESC';
}

export async function fetchRetailerEstimates(q: EstimateListQuery): Promise<RetailerEstimatesResponse> {
  const params = new URLSearchParams({
    page: String(q.page), per_page: String(q.perPage), search: q.search, sort: q.sort, order: q.order,
  });
  const res = await fetch(`${API_URL}/api/retailer-admin-estimates.php?${params}`, { credentials: 'include' });
  return parse<RetailerEstimatesResponse>(res, 'list_failed');
}

export async function patchRetailerEstimate(id: number, patch: RetailerEstimatePatch): Promise<RetailerEstimateView> {
  const res = await csrfFetch(`${API_URL}/api/retailer-admin-estimates.php?id=${id}`, { method: 'PATCH', jsonBody: patch });
  return (await parse<{ estimate: RetailerEstimateView }>(res, 'patch_failed')).estimate;
}

/* ---------------------------------------------------------------------------
 * Analytics (shape shared with admin-analytics.php)
 * ------------------------------------------------------------------------- */

export interface AnalyticsCategoryRow {
  /** Stable identifier — translate by this, not by `label`. */
  key: string;
  /** English fallback label from the server. */
  label: string;
  count: number;
}

export interface AnalyticsPayload {
  generatedAt: string;
  cacheAgeSeconds: number;
  cacheTtlSeconds: number;
  /** IANA zone the hour / weekday buckets are in. */
  timezone?: string;
  totals: {
    estimates: number;
    firstAt: string | null;
    lastAt: string | null;
    elapsedDays: number;
    weeksElapsed: number;
    totalValueCAD: number;
  };
  calculationMode: AnalyticsCategoryRow[];
  designPreset: AnalyticsCategoryRow[];
  spaceType: AnalyticsCategoryRow[];
  hourOfDay: { hour: number; count: number; avgPerDay: number }[];
  dayOfWeek: { dow: number; label: string; count: number; avgPerWeek: number }[];
  costRangeCAD: AnalyticsCategoryRow[];
}

export async function fetchRetailerAnalytics(refresh = false): Promise<AnalyticsPayload> {
  const res = await fetch(`${API_URL}/api/retailer-admin-analytics.php${refresh ? '?refresh=1' : ''}`, { credentials: 'include' });
  return parse<AnalyticsPayload>(res, 'analytics_failed');
}

/* ---------------------------------------------------------------------------
 * Members (managers only)
 * ------------------------------------------------------------------------- */

export type MemberStatus = 'pending' | 'approved' | 'rejected';

export interface RetailerMemberRow {
  id: number;
  userId: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  status: MemberStatus;
  role: RetailerRole;
  roleSource: 'salesforce' | 'local' | 'default';
  /** How they were matched: salesforce | salesforce_parent | domain | invite | self | admin */
  source: string;
  matchedDomain: string | null;
  invited: boolean;
  /** The node this row is on (the active retailer or one of its sub-retailers). */
  retailerId: number;
  /** The whole node: every location, incl. ones added later. */
  allLocations: boolean;
  /** With allLocations on a top-level retailer: its sub-retailers too. */
  includeChildren: boolean;
  locations: RetailerLocationRef[];
  scope: MemberScope;
  createdAt: string;
  approvedBy: string | null;
  approvedAt: string | null;
  /** The signed-in manager's own row — no actions allowed on it. */
  isSelf: boolean;
}

/** What the caller may hand out: nodes they hold whole, and every store inside their access. */
export interface RetailerAssignable {
  nodes: { id: number; name: string; parentId: number | null; whole: boolean; includeChildren: boolean }[];
  locations: { id: number; name: string; city: string | null; retailerId: number; retailerName?: string }[];
}

/** One tick in the access editor. */
export type RetailerAccessGrant =
  | { type: 'retailer'; id: number; includeChildren?: boolean }
  | { type: 'location'; id: number };

export interface RetailerMembersResponse {
  members: RetailerMemberRow[];
  counts: Record<MemberStatus, number>;
  /** Stores of the active node the caller may hand out (older shape; see assignable). */
  locations: RetailerLocationRef[];
  assignable?: RetailerAssignable;
}

export type RetailerMemberAction =
  | { action: 'approve'; id: number }
  | { action: 'reject'; id: number }
  | { action: 'set_role'; id: number; role: RetailerRole }
  | { action: 'set_locations'; id: number; locationIds: number[]; allLocations: boolean }
  | { action: 'set_access'; id: number; role: RetailerRole; grants: RetailerAccessGrant[] };

export async function fetchRetailerMembers(): Promise<RetailerMembersResponse> {
  const res = await fetch(`${API_URL}/api/retailer-admin-members.php`, { credentials: 'include' });
  return parse<RetailerMembersResponse>(res, 'members_failed');
}

/** Throws an Error whose message is the server code (e.g. 'last_manager', 'self_row'). */
export async function retailerMemberAction(body: RetailerMemberAction): Promise<RetailerMemberRow> {
  const res = await csrfFetch(`${API_URL}/api/retailer-admin-members.php`, { jsonBody: body });
  return (await parse<{ member: RetailerMemberRow }>(res, 'action_failed')).member;
}

/* ---------------------------------------------------------------------------
 * Locations (stores, child locations, team) — every approved member
 * ------------------------------------------------------------------------- */

/** retailer = no location assigned but the retailer has none to assign: sees the whole retailer. */
export type MemberScope = 'all' | 'locations' | 'none';

export interface RetailerLocationsResponse {
  stores: { id: number; name: string; kind: 'retailer' | 'installer'; city: string | null; region: string | null; retailerId: number; retailerName: string; isChild: boolean; memberCount: number; mine: boolean }[];
  children: { id: number; name: string; included: boolean }[];
  team: { id: number; firstName: string | null; lastName: string | null; email: string | null; role: RetailerRole; retailerId: number; retailerName: string; isChild: boolean; allLocations: boolean; includeChildren: boolean; locations: { id: number; name: string }[]; scope: MemberScope; isSelf: boolean }[];
}

export async function fetchRetailerLocations(): Promise<RetailerLocationsResponse> {
  const res = await fetch(`${API_URL}/api/retailer-admin-locations.php`, { credentials: 'include' });
  return parse<RetailerLocationsResponse>(res, 'locations_failed');
}

export async function fetchRetailerDashboard(): Promise<RetailerDashboardResponse> {
  const res = await fetch(`${API_URL}/api/retailer-admin-dashboard.php`, { credentials: 'include' });
  return parse<RetailerDashboardResponse>(res, 'dashboard_failed');
}
